import { proposalInvolvesPlayer } from '../../../../utils/proposalParticipants'

export const parsePlayers = (players) => {
  if (Array.isArray(players)) return players
  if (typeof players === 'string') {
    try {
      const parsed = JSON.parse(players)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

export const getMatchSideForPlayer = (match, pid) => {
  const homePlayers = parsePlayers(match?.home_players)
  const awayPlayers = parsePlayers(match?.away_players)
  const isHome = homePlayers.some((p) => Number(p?.id) === Number(pid))
  const isAway = awayPlayers.some((p) => Number(p?.id) === Number(pid))
  if (isHome)
    return { side: 'home', sameSide: homePlayers, oppSide: awayPlayers }
  if (isAway)
    return { side: 'away', sameSide: awayPlayers, oppSide: homePlayers }

  if (match?.home_player_id === pid) {
    const opp = match.away_player_id
      ? [
          {
            id: match.away_player_id,
            first_name: match.away_first_name,
            last_name: match.away_last_name,
          },
        ]
      : []
    return { side: 'home', sameSide: [{ id: pid }], oppSide: opp }
  }

  const opp = match?.home_player_id
    ? [
        {
          id: match.home_player_id,
          first_name: match.home_first_name,
          last_name: match.home_last_name,
        },
      ]
    : []
  return { side: 'away', sameSide: [{ id: pid }], oppSide: opp }
}

/**
 * Build a combined display name for a doubles team like "Carol Smith & David Jones".
 * The page-target player (if present) is listed first so the heading on a player
 * history page reads naturally. Returns null when there is not enough info to
 * construct a combined name (e.g. singles, missing data).
 *
 * @param {Array<{id:any, first_name?:string, last_name?:string}>} teamPlayers
 * @param {number|string|null|undefined} targetPlayerId
 * @returns {string|null}
 */
export const buildOpponentTeamDisplayName = (teamPlayers, targetPlayerId) => {
  if (!Array.isArray(teamPlayers) || teamPlayers.length < 2) return null
  const targetId = Number(targetPlayerId)
  const ordered = [...teamPlayers].sort((a, b) => {
    if (Number(a?.id) === targetId) return -1
    if (Number(b?.id) === targetId) return 1
    return 0
  })
  const names = ordered
    .map((p) => [p?.first_name, p?.last_name].filter(Boolean).join(' '))
    .filter((name) => name.length > 0)
  if (names.length < 2) return null
  return names.join(' & ')
}

/**
 * Build the opposing-team identity sub-object for a player history page from
 * the filtered opponent availability slots. The hook calls this to surface
 * `team: { team_id, display_name, players }` on processedOpponents for
 * doubles, or `null` for singles. Returns `null` when no doubles slot with a
 * resolvable team is present.
 *
 * @param {Array<Object>} slots - opponentAvailability slots (already filtered
 *   to the page-target user)
 * @param {number|string|null|undefined} targetPlayerId - the page-target
 *   player's id, used to order the combined display name
 * @returns {{ team_id: number|null, display_name: string|null, players: Array }|null}
 */
export const buildOpponentTeamFromSlots = (slots, targetPlayerId) => {
  if (!Array.isArray(slots)) return null
  const doublesSlot = slots.find(
    (slot) =>
      Array.isArray(slot?.opponent_team_players) &&
      slot.opponent_team_players.length > 1,
  )
  if (!doublesSlot) return null
  return {
    team_id: doublesSlot.opponent_team_id ?? null,
    players: doublesSlot.opponent_team_players,
    display_name: buildOpponentTeamDisplayName(
      doublesSlot.opponent_team_players,
      targetPlayerId,
    ),
  }
}

export const processOpponentSlots = (slots) => {
  const now = new Date()
  const futureSlots = slots.filter((slot) => new Date(slot.start_time) > now)

  if (futureSlots.length === 0) {
    return []
  }

  const sortedSlots = futureSlots.sort(
    (a, b) => new Date(a.start_time) - new Date(b.start_time),
  )

  const combinedSlots = []
  let currentCombined = null

  sortedSlots.forEach((slot) => {
    const slotStart = new Date(slot.start_time)
    const slotEnd = new Date(slot.end_time)

    if (isNaN(slotStart.getTime()) || isNaN(slotEnd.getTime())) {
      console.warn('Invalid date in slot:', slot)
      return
    }

    if (!currentCombined) {
      currentCombined = {
        start_time: slot.start_time,
        end_time: slot.end_time,
        date: slotStart.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        originalSlots: [slot],
      }
      return
    }

    const currentEnd = new Date(currentCombined.end_time)
    const sameDay =
      slotStart.toDateString() ===
      new Date(currentCombined.start_time).toDateString()

    if (sameDay && slotStart <= currentEnd) {
      if (slotEnd > currentEnd) {
        currentCombined.end_time = slot.end_time
      }
      currentCombined.originalSlots.push(slot)
      return
    }

    combinedSlots.push(currentCombined)
    currentCombined = {
      start_time: slot.start_time,
      end_time: slot.end_time,
      date: slotStart.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      originalSlots: [slot],
    }
  })

  if (currentCombined) {
    combinedSlots.push(currentCombined)
  }

  return combinedSlots
}

export const checkUserOverlap = (combinedSlot, userAvailability) => {
  const slotStart = new Date(combinedSlot.start_time)
  const slotEnd = new Date(combinedSlot.end_time)

  return userAvailability.some((userSlot) => {
    const userStart = new Date(userSlot.start_time)
    const userEnd = new Date(userSlot.end_time)
    const sameDay = slotStart.toDateString() === userStart.toDateString()

    return sameDay && slotStart < userEnd && slotEnd > userStart
  })
}

const isWithinOneHour = (time, baseDate, baseHour) => {
  const base = new Date(baseDate)
  base.setHours(baseHour, 0, 0, 0)
  const timeDiff = Math.abs(time.getTime() - base.getTime())
  return timeDiff < 3600000
}

const isWithinSlotWindow = (time, slotStart, slotEnd) => {
  const start = new Date(slotStart)
  const end = new Date(slotEnd)

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return false

  return time >= start && time < end
}

const proposalMatchesOpponent = (proposal, opponentId) =>
  proposalInvolvesPlayer(proposal, opponentId)

export const findMatchProposalForOpponent = (
  matchProposals,
  opponentId,
  date,
  hour,
  slotEndTime = null,
) => {
  return matchProposals.find((proposal) => {
    if (proposal.status === 'canceled' || proposal.status === 'deleted') {
      return false
    }

    if (!proposalMatchesOpponent(proposal, opponentId)) return false

    const proposalTime = new Date(proposal.proposed_time)
    if (slotEndTime) {
      return isWithinSlotWindow(proposalTime, date, slotEndTime)
    }

    return isWithinOneHour(proposalTime, date, hour)
  })
}

export const findLatestProposalForOpponent = (matchProposals, opponentId) => {
  let latestProposal = null
  let latestTime = 0

  matchProposals.forEach((proposal) => {
    if (proposal.status === 'canceled' || proposal.status === 'deleted') {
      return
    }

    if (!proposalMatchesOpponent(proposal, opponentId)) return

    const proposedTime = proposal.proposed_time
      ? new Date(proposal.proposed_time).getTime()
      : 0

    if (!latestProposal || proposedTime > latestTime) {
      latestProposal = proposal
      latestTime = proposedTime
    }
  })

  return latestProposal
}

export const findScheduledMatchForOpponent = (
  allMatches,
  opponentId,
  date,
  hour,
) => {
  return allMatches.find((match) => {
    if (!match.match_datetime) return false

    const matchTime = new Date(match.match_datetime)
    const isOpponentMatch =
      match.home_player_id === opponentId || match.away_player_id === opponentId

    return isWithinOneHour(matchTime, date, hour) && isOpponentMatch
  })
}

export const findExistingMatchWithOpponent = (allMatches, opponentId) => {
  const match = allMatches.find((candidate) => {
    if (candidate.status === 'created') return false

    return (
      candidate.home_player_id === opponentId ||
      candidate.away_player_id === opponentId
    )
  })

  return match || null
}
